import React, { FC, memo, useCallback, useMemo } from 'react';
import {
  Page as PDFPage,
  View,
  Document,
  StyleSheet,
  Image
} from '@react-pdf/renderer';
import {
  ViewQuotationBuildInfo,
  ViewQuotationHeaderSlip,
  ViewQuotationItems,
  ViewQuotationSlipFooter
} from './QuotationScreenShotComponent';
import {
  NewBranchData,
  TemplateDetails,
  TemplateProductDetails
} from 'src/types';
import { cloneDeep, forEach } from 'lodash';
import { promoPriceToSRP } from 'src/utils';

const defaultQuotationTemplate: TemplateDetails = {
  id: 0,
  name: 'PC Worth',
  raw_name: 'PC Worth',
  total_amount: 0,
  products: [
    {
      quantity: 0,
      product: {
        product_id: 0,
        product_name: '',
        retail_price: 0,
        category_id: 0,
        category_name: ''
      }
    }
  ],
  pc_build: 0,
  boost: 0,
  created_by: 'PC Worth',
  created_date: '',
  updated_by: '',
  updated_date: '',
  branch_id: 1
};

const styles = StyleSheet.create({
  page: {
    flexDirection: 'row'
  },
  parentView: {
    flexDirection: 'column',
    padding: 10,
    width: '100%'
  },
  closeButton: {
    position: 'absolute',
    right: 5,
    top: 5
  },
  pageBackground: {
    position: 'absolute',
    minWidth: '100%',
    minHeight: '100%',
    display: 'flex',
    height: '100%',
    width: '100%',
    opacity: 0.05
  }
});
// src=""
interface Props {
  isOpen: boolean;
  quotationTemplateItems?: TemplateDetails;
  branchInfo?: NewBranchData;
  userBranchOptions?: { name: string; value: number }[];
  showSRP?: boolean;
  onHandleClose: () => void;
}

const pageSize = 17;

const component: FC<Props> = ({
  quotationTemplateItems = defaultQuotationTemplate,
  showSRP = false,
  branchInfo
}) => {
  const itemTotalAmountSRP = useMemo(() => {
    let total = 0;
    if (quotationTemplateItems && quotationTemplateItems?.products) {
      forEach(quotationTemplateItems?.products, (item) => {
        const item_price = promoPriceToSRP(item?.product?.retail_price);
        total += item.quantity * item_price;

        return total;
      });
    }

    //total zero
    return total;
  }, [quotationTemplateItems]);

  const itemsPerPage = useMemo(() => {
    const cloneQuotationProducts = cloneDeep(quotationTemplateItems?.products);
    if (!cloneQuotationProducts) {
      return [];
    }

    const productItemsPerPage: TemplateProductDetails[] = cloneQuotationProducts?.reduce(
      (
        accumulator: TemplateProductDetails | any,
        value: TemplateProductDetails,
        index
      ) => {
        //if index is equal to 1 or so on then it becomes next page index
        //meaning there is more item than pageSize
        const pageIndex = Math.floor(index / pageSize);
        //check if accumulator is the current index else assign new array to new accumulator index
        accumulator[pageIndex] = accumulator[pageIndex] || [];
        accumulator[pageIndex].push(value);

        return accumulator;
      },
      []
    );
    return productItemsPerPage;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationTemplateItems?.products]);

  const Watermark = useCallback(() => {
    return Array.from({
      length: Math.ceil((quotationTemplateItems?.products?.length || 6) / 6)
    }).flatMap((_, row) =>
      Array.from({ length: 1 }).map((_, col) => (
        <Image
          key={`${row}-${col}`}
          src="/static/images/quotation-background.png"
          style={{
            position: 'absolute',
            top: row * 430,
            left: 0,
            width: 400,
            height: 430,
            opacity: 0.05 // light watermark
          }}
        />
      ))
    );
  }, [quotationTemplateItems]);

  return (
    <Document title={'Quotation Slip'}>
      <PDFPage size={{ height: undefined, width: 363 }} style={styles.page}>
        <View style={[styles.parentView]}>
          {Watermark()}

          <ViewQuotationHeaderSlip
            quotationData={quotationTemplateItems}
            branchInfo={branchInfo}
          />
          <ViewQuotationBuildInfo
            branchInfo={branchInfo}
            quotationData={quotationTemplateItems}
          />
          <ViewQuotationItems
            templateProducts={quotationTemplateItems?.products || []}
            showSRP={showSRP}
          />
          <ViewQuotationSlipFooter
            pageSize={itemsPerPage?.length}
            totalAmount={
              showSRP
                ? itemTotalAmountSRP
                : quotationTemplateItems?.total_amount
            }
          />
        </View>
      </PDFPage>
    </Document>
  );
};

export const QuotationScreenShotModal = memo(component);
